/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { observable, action } from "mobx";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { digSigStatus, AJSMetadataStatus, DIGSIG_VALIDATE_BUTTON_STATE } from "constants/enums";
import { DIGSIG_VALIDATE_RETRY_TIMEOUT, DIG_SIG_TYPE, VALIDATION_STATUS } from "constants/constants";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { getAJSMetadataStatus } from "selectors/documentSelectors";
import {
  consolidateValidationResults,
  getUnsupportedSignatures,
  pdfHasUnsupportedSignature,
  isAdobeSignAPIError
} from "utils/DigSigUtils";
import { getEnableDigSig, getDigSigRHPVisibility } from "selectors/digSigSelectors";
import * as stores from "stores";
import docApi from "api";
import { showDigSigBanner } from "utils/BannerUtils";
import { triggerDigSigValidationStatusUpdatedEvent } from "publicApis/Events/events";
import { showDocPermissionsToast } from "utils/DocPermissionsUtils";
import { getIntl } from "selectors/appSelectors";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  trackDigSigValidationTriggered,
  trackDigSigValidationTimeTaken,
  trackDigSigTypes,
  trackDigSigLTV,
  trackDigSigSubFilters,
  trackDigSigValidatedBy,
  trackDigSigTrustSources,
  trackDigSigCount,
  trackDigSigValidationStatus,
  trackDigSigUnsupportedSignatures,
  trackDigSigShowValidateButton,
  trackDocumentPermissionShow
} from "analytics/events";

export class DigSigStore {
  @observable digSigFetchStatus;
  @observable enableDigSig;
  @observable showDigSigRHP;
  @observable digSigBannerVisibility;
  @observable validateDigSigButtonState;
  @observable validationAttempt;
  @observable pdfValidationSummary;
  externalCertificateValidator;
  signaturesData;
  digSigBannerId;
  initializeOpts = {};
  shouldShowExpandedComponent = false;

  constructor() {
    this.init();
    this.verifyDigSig = this.verifyDigSig.bind(this);
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    subscribeToSelector(stores, isInitialPageLoaded, this.triggerDigSigValidation.bind(this));
    this.digSigFetchStatus = digSigStatus.NOT_PRESENT;
    this.enableDigSig = false;
    this.externalCertificateValidator = null;
    this.signaturesData = {};
    this.showDigSigRHP = false;
    this.digSigBannerVisibility = true;
    this.validateDigSigButtonState = DIGSIG_VALIDATE_BUTTON_STATE.HIDDEN;
    this.validationAttempt = 0;
    this.pdfValidationSummary = null;
    this.digSigBannerId = null;
    this.analyticsAdded = false;
  }

  @action
  setDigSigBannerVisibility(value) {
    this.digSigBannerVisibility = value;
  }

  setExternalCertificateValidator(value) {
    this.externalCertificateValidator = value;
  }

  setDigSigBannerId(value) {
    this.digSigBannerId = value;
  }

  @action
  setDigSigRHPVisibility(value) {
    this.showDigSigRHP = value;
  }

  @action
  setEnableDigSig(value) {
    this.enableDigSig = value;
  }

  @action
  setDigSigFetchStatus(status) {
    this.digSigFetchStatus = status;
  }

  @action
  triggerDigSigValidation() {
    if (
      !isCurrentDeviceMobile() &&
      !isCurrentDeviceTablet() &&
      getAJSMetadataStatus(stores) === AJSMetadataStatus.SUCCEEDED &&
      isInitialPageLoaded(stores) === true
    ) {
      if (getEnableDigSig(stores)) {
        this.setDigSigRHPVisibility(true);
        this.setDigSigFetchStatus(digSigStatus.PRESENT_AND_NOT_FETCHED);
        this.verifyDigSig();
      } else {
        this.setDigSigFetchStatus(digSigStatus.NOT_PRESENT);
      }
    }
  }

  @action
  incrementValidationAttempt() {
    this.validationAttempt += 1;
  }

  @action
  setValidateDigSigButtonState(value) {
    this.validateDigSigButtonState = value;
  }

  setShowValidateDigSigButton(value) {
    if (value) {
      this.setValidateDigSigButtonState(DIGSIG_VALIDATE_BUTTON_STATE.DISABLE);
      setTimeout(() => {
        this.setValidateDigSigButtonState(DIGSIG_VALIDATE_BUTTON_STATE.ENABLE);
      }, DIGSIG_VALIDATE_RETRY_TIMEOUT);
    } else {
      this.setValidateDigSigButtonState(DIGSIG_VALIDATE_BUTTON_STATE.HIDDEN);
    }
  }

  @action
  setPdfValidationSummary(value) {
    this.pdfValidationSummary = value;
  }

  _addAnalytics(data) {
    if (this.analyticsAdded) {
      return;
    }

    const types = new Set();
    const validators = new Set();
    const trustSources = new Set();
    const subFilters = new Set();

    data.signatures.forEach(signature => {
      const { signatureDetails, digestValidation, certificateValidation } = signature;
      types.add(signatureDetails.type);
      subFilters.add(digestValidation.subfilter);
      signatureDetails.ltv && trackDigSigLTV();

      const { verifiedBy, trustSources: trustSourcesArray } = certificateValidation;
      verifiedBy && validators.add(verifiedBy);
      trustSourcesArray.forEach(trustSource => {
        trustSource && trustSources.add(trustSource);
      });
    });

    trackDigSigTypes(Array.from(types));
    trackDigSigSubFilters(Array.from(subFilters));
    trackDigSigValidatedBy(Array.from(validators));
    trackDigSigTrustSources(Array.from(trustSources));
    trackDigSigCount(data.signatures.length);
    this.analyticsAdded = true;
  }

  processSignaturesData(data) {
    this._addAnalytics(data);

    // remove usage rights signature from the list
    data.signatures = data.signatures.filter(
      signature => signature.signatureDetails.type !== DIG_SIG_TYPE.USAGE_RIGHTS
    );
  }

  setSignaturesData(data) {
    if (!data || !data.signatures.length) {
      return;
    }

    this.processSignaturesData(data);
    this.signaturesData = data;

    data.pdfValidationSummary = consolidateValidationResults(data.signatures);
    data.pdfValidationSummary.unsupportedSignatures = getUnsupportedSignatures(data.signatures);

    // disable digital signature if the PDF has an unsupported signature
    if (pdfHasUnsupportedSignature(data.signatures)) {
      this.setDigSigRHPVisibility(false);
      trackDigSigUnsupportedSignatures(data.pdfValidationSummary.unsupportedSignatures);
    }

    if (isAdobeSignAPIError(data.signatures) && this.validationAttempt < 4) {
      this.setShowValidateDigSigButton(true);
      const intl = getIntl(stores);
      data.pdfValidationSummary.message = intl.formatMessage({ id: "digsig.pdfValidation.retryValidation" });
      trackDigSigShowValidateButton();
    }

    this.setPdfValidationSummary(data.pdfValidationSummary);
    showDigSigBanner();
    triggerDigSigValidationStatusUpdatedEvent(data);

    if (data.pdfValidationSummary.status === VALIDATION_STATUS.VALID && this.validationAttempt === 1) {
      showDocPermissionsToast();
      trackDocumentPermissionShow();
    }
    trackDigSigValidationStatus(data.pdfValidationSummary);
  }

  verifyDigSig() {
    return Promise.all([docApi.isCompletePDFBufferAvailable(), initialPageLoadedPromise(stores)])
      .then(async () => {
        this.setShowValidateDigSigButton(false);
        this.setDigSigFetchStatus(digSigStatus.PRESENT_AND_FETCHING);

        this.incrementValidationAttempt();
        trackDigSigValidationTriggered(this.validationAttempt);

        const start = performance.now();
        const response = await this.docApi.verifyDigSig(this.externalCertificateValidator);
        const end = performance.now();

        trackDigSigValidationTimeTaken(end - start);

        this.setSignaturesData(response.data);
        const { signatures } = response.data;

        if (!signatures.length) {
          this.setDigSigFetchStatus(digSigStatus.NOT_PRESENT);
          this.setDigSigRHPVisibility(false);
        } else {
          this.setDigSigFetchStatus(digSigStatus.PRESENT_AND_FETCHED);
        }
      })
      .catch(() => {
        this.setDigSigFetchStatus(digSigStatus.NOT_PRESENT);
        this.setDigSigRHPVisibility(false);
      });
  }

  setInitializeOpts(opts, digSigComponentLoader) {
    this.initializeOpts = opts;
    this.digSigComponentLoader = digSigComponentLoader;
  }

  initializeAction() {
    const updateShouldShowDigSigDropin = shouldShowDigSig => {
      this.initializeOpts.showCallback && this.initializeOpts.showCallback(shouldShowDigSig);
      this.shouldShowExpandedComponent = shouldShowDigSig;

      if (shouldShowDigSig) {
        this.digSigComponentLoader && this.digSigComponentLoader();
      }
    };

    updateShouldShowDigSigDropin(false);
    subscribeToSelector(stores, getDigSigRHPVisibility, enable => {
      updateShouldShowDigSigDropin(enable);
    });
  }
}
